import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * "I just met someone" — mobile-first capture on one screen. A first name is the only
 * requirement; an event suggestion fills how/where we met by reference. Save first, enrich
 * later.
 */
export default function QuickCaptureDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ first_name: "", last_name: "", where_text: "", how_we_met: "", note: "",
      email: "", event_id: null });
    api.get("/crm/quick-capture/context")
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickEvent = (ev) => setForm((f) => (f.event_id === ev.event_id
    ? { ...f, event_id: null, where_text: "", how_we_met: "" }
    : { ...f, event_id: ev.event_id, where_text: ev.where || "",
      how_we_met: `Met at ${ev.title}` }));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/crm/quick-capture", {
        first_name: form.first_name, last_name: form.last_name,
        how_we_met: form.how_we_met, where_text: form.where_text,
        event_id: form.event_id, note: form.note,
        methods: form.email ? [{ kind: "email", value: form.email, label: "work",
          preferred: true }] : [],
      });
      toast.success("Saved — you can add more details any time");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "crm_first_name_required"
        ? "A first name is required" : "Couldn't save that contact");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md"
        data-testid="quick-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" /> I just met someone
          </DialogTitle>
          <DialogDescription>
            Just the name is enough. Everything else can wait until later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="qc-first">First name</Label>
              <Input id="qc-first" data-testid="qc-first-name" value={form.first_name || ""}
                onChange={set("first_name")} maxLength={75} autoFocus autoComplete="off"
                className="h-11 text-base" />
            </div>
            <div>
              <Label htmlFor="qc-last">Last name</Label>
              <Input id="qc-last" data-testid="qc-last-name" value={form.last_name || ""}
                onChange={set("last_name")} maxLength={75} className="h-11 text-base" />
            </div>
          </div>

          {events.length > 0 && (
            <div data-testid="qc-event-suggestions">
              <span className="mb-1.5 block text-sm font-[560]">Are you here right now?</span>
              <div className="flex flex-wrap gap-1.5">
                {events.map((ev) => (
                  <button key={ev.event_id} type="button" onClick={() => pickEvent(ev)}
                    aria-pressed={form.event_id === ev.event_id}
                    data-testid={`qc-event-${ev.event_id}`}
                    className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                      form.event_id === ev.event_id ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground/70 hover:bg-secondary/80"}`}>
                    {ev.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="qc-where">Where we met</Label>
            <Input id="qc-where" data-testid="qc-where" value={form.where_text || ""}
              onChange={set("where_text")} maxLength={150} className="h-11 text-base" />
          </div>
          <div>
            <Label htmlFor="qc-how">How we met</Label>
            <Input id="qc-how" data-testid="qc-how" value={form.how_we_met || ""}
              onChange={set("how_we_met")} maxLength={300} className="h-11 text-base" />
          </div>
          <div>
            <Label htmlFor="qc-email">Email (optional)</Label>
            <Input id="qc-email" data-testid="qc-email" type="email" value={form.email || ""}
              onChange={set("email")} maxLength={254} className="h-11 text-base" />
          </div>
          <div>
            <Label htmlFor="qc-note">Remember this</Label>
            <Textarea id="qc-note" data-testid="qc-note" rows={3} maxLength={10000}
              value={form.note || ""} onChange={set("note")}
              placeholder="What should you remember? Private to you." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="qc-cancel">
            Cancel
          </Button>
          <Button onClick={save} data-testid="qc-save" className="h-11"
            disabled={busy || !(form.first_name || "").trim()}>
            {busy ? "Saving…" : "Save contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
